import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import useDebounce from "../../../hooks/useDebounce";
import TabFormWrapper from "../../Shared/TabFormWrapper";
import FormFooter from "../../Shared/FormFooter";
import InputInlineAddon from "../../Shared/InputInlineAddon";
import Toggle from "../../Shared/Toggle";
import StatusIndicator from "../../Shared/StatusIndicator";
import AlertModal from "../../Shared/AlertModal";
import AlertRed from "../../Shared/AlertRed";
import AlertGreen from "../../Authentication/AlertGreen";
import VerifyCodeInput from "../../Authentication/VerifyCodeInput";
import BookingPageFormSkeleton from "../../Shared/LoadingSkeletons/BookingPageFormSkeleton";
import QRCodeDisplay from "../../Shared/QRCodeDisplay";
import { COLOUR } from "../../../config/constants";
import { checkSlugAvailability } from "../../../api/bookingPageApi";
import { resendVerificationEmail } from "../../../api/authApi";
import { useResendTimer } from "../../../hooks/useResendTimer";
import {
  useBookingPage,
  useUpdateBookingPageTab,
} from "../../../hooks/useBookingPage";
import { useSetupNavigation } from "../../../hooks/useSetupNavigation";
import { useEmailVerification } from "../../../hooks/useEmailVerification";
import { useAuth } from "../../../contexts/AuthContext";
import { getBookingUrlPrefix } from "../../../utility/environment";
import { getBrandName } from "../../../utility/branding";
import { generateSlugSuggestion } from "../../../utility/slugUtils";

const BookingPageForm = () => {
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [isNewUserActivation, setIsNewUserActivation] = useState(false);
  const [showSlugChangeWarning, setShowSlugChangeWarning] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState(null);
  const [showToggleConfirmation, setShowToggleConfirmation] = useState(false);
  const [pendingToggleState, setPendingToggleState] = useState(null);
  const [modalContent, setModalContent] = useState({
    title: "",
    message: "",
    primaryCaption: "",
    primaryButtonColour: "blue",
  });
  const [slugValidation, setSlugValidation] = useState({
    isValid: true,
    isAvailable: true,
    isChecking: false,
    message: "",
  });
  const [isCopied, setIsCopied] = useState(false);
  const [showQrPopover, setShowQrPopover] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const qrButtonRef = useRef(null);
  const qrPopoverRef = useRef(null);

  // Inline email verification code entry (replaces the old blocking modal)
  const [showVerificationCodeEntry, setShowVerificationCodeEntry] = useState(false);
  const [verificationSendError, setVerificationSendError] = useState(null);
  // Stores the form data that was pending when verification was triggered,
  // so we can resume the save after a successful code verify.
  const [pendingActivationData, setPendingActivationData] = useState(null);

  const { data: configData, isLoading } = useBookingPage();
  const { handleSetupStepComplete, isSetupComplete } = useSetupNavigation();
  const { isEmailVerified } = useEmailVerification();
  const { user } = useAuth();
  const { startCooldown } = useResendTimer(120, "verifyCodeResendTimer:bookingGate");

  // Sends the OTP on demand when an unverified user tries to go live, then reveals
  // the inline code entry. Only fired from the activation intercept — never on mount.
  const { mutate: sendVerificationCode, isPending: isSendingCode } = useMutation({
    mutationFn: () => resendVerificationEmail({ email: user?.email }),
    onSuccess: () => {
      setVerificationSendError(null);
      startCooldown();
      setShowVerificationCodeEntry(true);
    },
    onError: () => {
      setVerificationSendError(
        "Couldn't send a verification code. Please try again."
      );
    },
  });

  const { mutate: updateGeneral, isPending: isUpdating } =
    useUpdateBookingPageTab("general", {
      onSuccess: async () => {
        if (!isSetupComplete) {
          await handleSetupStepComplete(
            "booking-page",
            "Booking page updated successfully"
          );
        }
      },
    });

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    getValues,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      customSlug: "",
      isActive: false,
    },
  });

  const originalSlug = configData?.data?.customSlug;
  const currentSlug = watch("customSlug");
  const debouncedSlug = useDebounce(currentSlug, 500);
  const isActive = watch("isActive");

  const dynamicBookingUrl = currentSlug
    ? getBookingUrlPrefix() + currentSlug
    : null;

  const businessName =
    configData?.data?.businessName ||
    configData?.data?.providerName ||
    getBrandName();

  const [isSlugSuggested, setIsSlugSuggested] = useState(false);

  // Close QR popover on outside click
  useEffect(() => {
    if (!showQrPopover) return;
    const handleClickOutside = (e) => {
      if (
        qrPopoverRef.current &&
        !qrPopoverRef.current.contains(e.target) &&
        qrButtonRef.current &&
        !qrButtonRef.current.contains(e.target)
      ) {
        setShowQrPopover(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showQrPopover]);

  // Set form values when data loads
  useEffect(() => {
    if (configData?.data) {
      let slugValue = configData.data.customSlug || "";

      if (!configData.data.customSlug) {
        const suggestion = generateSlugSuggestion(
          configData.data.businessName ||
            configData.data.providerName ||
            (user?.firstName && user?.lastName
              ? `${user.firstName} ${user.lastName}`
              : "")
        );
        if (suggestion && suggestion.length >= 3) {
          slugValue = suggestion;
          setIsSlugSuggested(true);
        }
      }

      const formData = {
        customSlug: configData.data.customSlug || "",
        isActive: configData.data.isActive || false,
      };
      reset(formData);

      if (!configData.data.customSlug && slugValue) {
        setValue("customSlug", slugValue, { shouldDirty: true });
      }
    }
  }, [configData, reset, user, setValue]);

  // Real-time slug validation
  useEffect(() => {
    const controller = new AbortController();

    const validateSlug = async () => {
      const slugToValidate = currentSlug;
      const hasChanged = originalSlug !== slugToValidate;
      const isEmptyWhenActive =
        (!slugToValidate || slugToValidate.trim() === "") && isActive;

      if (!hasChanged && !isEmptyWhenActive) {
        setSlugValidation({ isValid: true, isAvailable: true, isChecking: false, message: "" });
        return;
      }

      if (!slugToValidate || slugToValidate.trim() === "") {
        const message = isActive ? "URL is required when page is active" : "";
        setSlugValidation({ isValid: !isActive, isAvailable: true, isChecking: false, message });
        return;
      }

      if (!/^[a-z0-9-]+$/.test(slugToValidate)) {
        setSlugValidation({ isValid: false, isAvailable: false, isChecking: false, message: "Use only lowercase letters, numbers, and dashes" });
        return;
      }
      if (slugToValidate.length < 3) {
        setSlugValidation({ isValid: false, isAvailable: false, isChecking: false, message: "Must be at least 3 characters" });
        return;
      }
      if (slugToValidate.length > 50) {
        setSlugValidation({ isValid: false, isAvailable: false, isChecking: false, message: "Must be 50 characters or fewer" });
        return;
      }
      if (/--/.test(slugToValidate)) {
        setSlugValidation({ isValid: false, isAvailable: false, isChecking: false, message: "Can't use two dashes in a row" });
        return;
      }
      if (slugToValidate.startsWith("-") || slugToValidate.endsWith("-")) {
        setSlugValidation({ isValid: false, isAvailable: false, isChecking: false, message: "Can't start or end with a dash" });
        return;
      }

      if (debouncedSlug === slugToValidate) {
        setSlugValidation((prev) => ({ ...prev, isChecking: true }));
        try {
          const response = await checkSlugAvailability(slugToValidate);
          if (controller.signal.aborted) return;
          setSlugValidation({
            isValid: true,
            isAvailable: response.data.isAvailable,
            isChecking: false,
            message: response.data.isAvailable ? "This link is available!" : "This link is already taken. Try another.",
          });
        } catch (error) {
          if (controller.signal.aborted) return;
          setSlugValidation({ isValid: false, isAvailable: false, isChecking: false, message: "Error checking availability" });
        }
      } else {
        setSlugValidation({ isValid: true, isAvailable: true, isChecking: true, message: "Checking availability..." });
      }
    };

    validateSlug();
    return () => controller.abort();
  }, [currentSlug, debouncedSlug, originalSlug, isActive]);

  const handleSlugChange = (newSlug) => {
    setValue("customSlug", newSlug, { shouldDirty: true });
    setErrors({});
    setIsSlugSuggested(false);
  };

  const handleCopyUrl = async () => {
    if (!dynamicBookingUrl) return;
    try {
      await navigator.clipboard.writeText(dynamicBookingUrl);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy URL:", err);
    }
  };

  // Toggle handlers
  const isToggleDisabled = isActive
    ? false
    : !currentSlug || currentSlug.trim() === "" || !slugValidation.isValid || !slugValidation.isAvailable;

  // Single choke point: if a save would activate the page while the user is
  // unverified, stash the exact payload and show the inline code field instead of
  // letting the request reach the backend (which would 403 EMAIL_NOT_VERIFIED).
  // Returns true when it intercepts, so callers can bail.
  const interceptIfUnverifiedActivation = (data) => {
    if (data?.isActive === true && !isEmailVerified) {
      setPendingActivationData(data);
      // Guard against a double-fire (e.g. a rapid second toggle/submit while the
      // first send is still in flight) — that would send two codes and reset the
      // cooldown. If a send is already pending, the panel will reveal on its success.
      if (isSendingCode) {
        return true;
      }
      // Without a loaded user we have no address to send to; surface an error
      // rather than firing a request with email: undefined (which 500s the backend).
      if (!user?.email) {
        setVerificationSendError(
          "Your session isn't ready yet. Please refresh and try again."
        );
        return true;
      }
      // Send a fresh code on demand, then reveal the entry on success. The input
      // is only shown once a code has actually been sent (see sendVerificationCode
      // onSuccess) so the user never types into a field for a code that isn't coming.
      setVerificationSendError(null);
      sendVerificationCode();
      return true;
    }
    return false;
  };

  const handleToggleChange = (checked) => {
    if (checked && isToggleDisabled) return;
    if (interceptIfUnverifiedActivation({ isActive: checked, customSlug: currentSlug })) {
      return;
    }

    const content = {
      title: checked ? "Activate Booking Page" : "Take Booking Page Offline",
      message: checked
        ? "Your booking page will become live and accessible to clients. They will be able to book appointments immediately."
        : "Your booking page will no longer be accessible to clients. They won't be able to book new appointments until you reactivate it.",
      primaryCaption: checked ? "Go Live" : "Take Offline",
      primaryButtonColour: checked ? "blue" : "red",
    };

    setModalContent(content);
    setPendingToggleState(checked);
    setShowToggleConfirmation(true);
  };

  const performToggleChange = (checked) => {
    setValue("isActive", checked, { shouldDirty: true });
    setErrors({});
    setSuccess(false);

    const updateData = { isActive: checked };
    if (currentSlug) updateData.customSlug = currentSlug;

    updateGeneral(updateData, {
      onSuccess: () => {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      },
      onError: (err) => {
        setValue("isActive", !checked, { shouldDirty: true });
        const errorMessage = err.response?.data?.error?.message;
        setErrors({ title: "Error", message: errorMessage || "Failed to update booking page" });
      },
    });
  };

  const handleConfirmToggle = () => {
    if (pendingToggleState !== null) performToggleChange(pendingToggleState);
    setShowToggleConfirmation(false);
    setPendingToggleState(null);
  };

  const handleCancelToggle = () => {
    setShowToggleConfirmation(false);
    setPendingToggleState(null);
  };

  const handleSubmitWithVerification = async (data) => {
    const customSlug = data.customSlug;
    const isNewUser = !originalSlug;
    // New users who pick a slug are going live on save, even though the toggle
    // isn't shown to them — shape the payload to reflect that intent.
    const shouldAutoActivate = isNewUser && customSlug && customSlug.trim() !== "" && !data.isActive;
    const effectiveData = shouldAutoActivate ? { ...data, isActive: true } : data;

    if (effectiveData.isActive && originalSlug && (!customSlug || customSlug.trim() === "")) {
      setErrors({ title: "Can't Remove Link", message: "You can't remove your booking link while your page is live. Take it offline first." });
      return;
    }

    if (effectiveData.isActive && (!customSlug || customSlug.trim() === "")) {
      setErrors({ title: "Booking Link Required", message: "Choose a booking link name before going live." });
      return;
    }

    // Any activation by an unverified user is intercepted inline (covers new-user
    // "Save & Go Live", returning-user re-save of a live page, etc.).
    if (interceptIfUnverifiedActivation(effectiveData)) {
      return;
    }

    if (originalSlug && customSlug !== originalSlug && effectiveData.isActive) {
      setPendingSaveData(effectiveData);
      setShowSlugChangeWarning(true);
      return;
    }

    await performSave(effectiveData, isNewUser);
  };

  const onSubmit = handleSubmitWithVerification;

  const performSave = async (data, isNewUser = false) => {
    setErrors({});
    setSuccess(false);
    setIsNewUserActivation(false);

    updateGeneral(data, {
      onSuccess: () => {
        setSuccess(true);
        if (isNewUser && data.isActive) {
          setIsNewUserActivation(true);
          setTimeout(() => { setSuccess(false); setIsNewUserActivation(false); }, 4000);
        } else {
          setTimeout(() => setSuccess(false), 3000);
        }
      },
      onError: (err) => {
        const errorMessage = err.response?.data?.error?.message;
        const errorDetails = err.response?.data?.error?.details;
        let displayMessage = errorMessage || "Failed to update booking page settings";
        if (errorDetails && Array.isArray(errorDetails)) displayMessage = errorDetails.join(", ");
        if (errorMessage?.includes("already taken")) displayMessage = "This link is already taken. Please try a different one.";
        setErrors({ title: "Error", message: displayMessage });
      },
    });
  };

  /**
   * Called by VerifyCodeInput after a successful code verification.
   * Invalidates auth state so isEmailVerified flips, then continues
   * the pending activation submit without requiring a tab switch.
   */
  const handleVerifySuccess = () => {
    // VerifyCodeInput already invalidates ["auth"] and ["setupProgress"] before
    // calling this; no need to repeat them here.
    setShowVerificationCodeEntry(false);
    if (pendingActivationData) {
      const isNewUser = !originalSlug;
      // Merge the current form values over the stashed activation intent: the
      // intent flags (isActive, any auto-activate shaping) come from the snapshot,
      // but the slug and other fields are taken from the form as it is NOW — so an
      // edit the user made while the code-entry panel was open is not discarded.
      const dataToSave = { ...pendingActivationData, ...getValues(), isActive: true };
      performSave(dataToSave, isNewUser);
      setPendingActivationData(null);
    }
  };

  if (isLoading) {
    return (
      <TabFormWrapper>
        <BookingPageFormSkeleton showToggle={true} inputCount={1} showEnhancedSection={false} />
      </TabFormWrapper>
    );
  }

  return (
    <>
      {/* Slug Change Warning Modal */}
      <AlertModal
        isOpen={showSlugChangeWarning}
        modalTitle="Change Your Booking Link"
        modalMessage={
          <>
            <p className="mb-3">You're about to change your booking link:</p>
            <div className="bg-cream-100 rounded-md p-3 mb-3 space-y-2">
              <div className="flex items-center text-sm">
                <span className="text-ink-500 w-12 shrink-0">Old:</span>
                <span className="font-mono text-signal-red-600 line-through break-all">{getBookingUrlPrefix()}{originalSlug}</span>
              </div>
              <div className="flex items-center text-sm">
                <span className="text-ink-500 w-12 shrink-0">New:</span>
                <span className="font-mono text-signal-green-600 break-all">{dynamicBookingUrl}</span>
              </div>
            </div>
            <p className="text-sm text-ink-600">
              The old link will stop working immediately. Anyone who bookmarked or saved your old link will see an error.
            </p>
          </>
        }
        primaryCaption="Save Changes"
        primaryButtonColour="blue"
        secondaryCaption="Cancel"
        primaryClickHandler={() => {
          const data = pendingSaveData;
          setShowSlugChangeWarning(false);
          setPendingSaveData(null);
          if (!data) return;
          // Defensive: never publish a slug change for an unverified user.
          if (interceptIfUnverifiedActivation(data)) return;
          performSave(data);
        }}
        dismissModalHandler={() => { setShowSlugChangeWarning(false); setPendingSaveData(null); }}
        showIcon={false}
      />

      {/* Toggle Confirmation Modal */}
      <AlertModal
        isOpen={showToggleConfirmation}
        modalTitle={modalContent.title}
        modalMessage={modalContent.message}
        primaryCaption={modalContent.primaryCaption}
        primaryButtonColour={modalContent.primaryButtonColour}
        secondaryCaption="Cancel"
        primaryClickHandler={handleConfirmToggle}
        dismissModalHandler={handleCancelToggle}
        showIcon={false}
      />

      {/* Mobile QR Modal */}
      <AlertModal
        isOpen={showQrModal}
        modalTitle="QR Code"
        modalMessage={
          <div className="flex justify-center py-2">
            <QRCodeDisplay url={dynamicBookingUrl} businessName={businessName} size={160} />
          </div>
        }
        primaryCaption="Done"
        primaryButtonColour="blue"
        primaryClickHandler={() => setShowQrModal(false)}
        dismissModalHandler={() => setShowQrModal(false)}
        showIcon={false}
      />

      <TabFormWrapper onSubmit={handleSubmit(onSubmit)}>
        <div className="px-4 py-6 sm:p-8 space-y-6">
          {!originalSlug ? (
            /* ===== NEW USER SETUP FLOW ===== */
            <>
              {/* Slug input */}
              <div>
                <label className="block text-sm font-medium text-ink-900 mb-1">Your Booking Link</label>
                <p className="text-sm text-ink-600 mb-3">
                  Choose a short, memorable link to share with clients — something simple like your business name.
                </p>
                <InputInlineAddon
                  prefix={getBookingUrlPrefix()}
                  mobilePrefix={getBookingUrlPrefix().replace(/^https?:\/\//, "")}
                  placeholder="your-business-name"
                  name="customSlug"
                  register={register}
                  onChange={(e) => handleSlugChange(e.target.value)}
                />
                {currentSlug && currentSlug.trim() !== "" && (
                  <p className="mt-1.5 sm:hidden text-xs text-ink-500 break-all">
                    Your link: <span className="font-medium text-ink-700">{dynamicBookingUrl}</span>
                  </p>
                )}
                {isSlugSuggested && currentSlug && (
                  <p className="mt-1.5 text-xs text-ink-500">We suggested this based on your name. Feel free to change it.</p>
                )}

                {/* Validation feedback */}
                {slugValidation.isChecking && (
                  <div className="mt-2 flex items-center text-sm text-ink-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent-500 mr-2"></div>
                    Checking availability...
                  </div>
                )}
                {!slugValidation.isChecking && slugValidation.message && currentSlug && currentSlug.trim() !== "" && (
                  <div className="mt-2">
                    <div className={`flex items-center text-sm ${slugValidation.isValid && slugValidation.isAvailable ? "text-signal-green-600" : "text-signal-red-600"}`}>
                      {slugValidation.isValid && slugValidation.isAvailable ? (
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      {slugValidation.message}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            /* ===== RETURNING USER MANAGEMENT FLOW ===== */
            <>
              {/* Block 1: Status header bar */}
              <div className="flex items-center justify-between pb-5 border-b border-ink-100">
                <StatusIndicator
                  colour={isActive ? COLOUR.GREEN : COLOUR.GRAY}
                  label={isActive ? "Live" : "Offline"}
                />
                <Toggle
                  checked={isActive}
                  onChange={handleToggleChange}
                  disabled={isToggleDisabled}
                />
              </div>

              {/* Block 2: Sharing card (live) or offline message */}
              {isActive && dynamicBookingUrl ? (
                <div className="bg-cream-100 rounded-lg border border-ink-100 p-4">
                  <p className="text-xs font-medium text-ink-500 uppercase tracking-wide">
                    Your booking link
                  </p>
                  <p className="mt-2 font-mono text-sm text-accent-600 break-all">
                    {dynamicBookingUrl}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    {/* Copy Link */}
                    <button
                      type="button"
                      onClick={handleCopyUrl}
                      className={`inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded border transition-colors ${
                        isCopied
                          ? "border-signal-green-500 bg-signal-green-50 text-accent-700"
                          : "border-ink-200 bg-white text-ink-700 hover:bg-cream-100"
                      }`}
                    >
                      {isCopied ? (
                        <>
                          <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Copied!
                        </>
                      ) : (
                        <>
                          <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          Copy Link
                        </>
                      )}
                    </button>

                    {/* Preview */}
                    <a
                      href={dynamicBookingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded border border-ink-200 bg-white text-ink-700 hover:bg-cream-100 transition-colors"
                    >
                      <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      Preview
                    </a>

                    {/* QR Code */}
                    <div className="relative">
                      <button
                        ref={qrButtonRef}
                        type="button"
                        onClick={() => {
                          if (window.innerWidth < 768) {
                            setShowQrPopover(false);
                            setShowQrModal(true);
                          } else {
                            setShowQrModal(false);
                            setShowQrPopover((prev) => !prev);
                          }
                        }}
                        className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded border border-ink-200 bg-white text-ink-700 hover:bg-cream-100 transition-colors"
                      >
                        <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                        </svg>
                        QR Code
                      </button>

                      {/* Desktop QR popover */}
                      {showQrPopover && (
                        <div
                          ref={qrPopoverRef}
                          className="hidden md:block absolute left-0 top-full mt-2 z-20 bg-white rounded-lg shadow-lg border border-ink-100 p-4"
                        >
                          <QRCodeDisplay url={dynamicBookingUrl} businessName={businessName} size={128} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-cream-100 rounded-lg border border-ink-100 p-4">
                  <p className="text-sm text-ink-500">
                    Your booking page is offline. Toggle the switch above to go live and start sharing your link.
                  </p>
                </div>
              )}

              {/* Block 3: Edit booking link */}
              <div className="pt-5 border-t border-ink-100">
                <h3 className="text-sm font-medium text-ink-900">Edit Booking Link</h3>
                <p className="text-xs text-ink-500 mt-1 mb-3">
                  Changing your link will break any previously shared URLs.
                </p>
                <InputInlineAddon
                  prefix={getBookingUrlPrefix()}
                  mobilePrefix={getBookingUrlPrefix().replace(/^https?:\/\//, "")}
                  placeholder="your-business-name"
                  name="customSlug"
                  register={register}
                  onChange={(e) => handleSlugChange(e.target.value)}
                />

                {/* Validation feedback */}
                {slugValidation.isChecking && (
                  <div className="mt-2 flex items-center text-sm text-ink-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent-500 mr-2"></div>
                    Checking availability...
                  </div>
                )}
                {!slugValidation.isChecking && slugValidation.message && currentSlug && currentSlug.trim() !== "" && (
                  <div className="mt-2">
                    <div className={`flex items-center text-sm ${slugValidation.isValid && slugValidation.isAvailable ? "text-signal-green-600" : "text-signal-red-600"}`}>
                      {slugValidation.isValid && slugValidation.isAvailable ? (
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      {slugValidation.message}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Inline email verification — sits directly above the publish action */}
        {showVerificationCodeEntry && (
          <div className="px-4 pb-4 sm:px-8">
            <div className="p-4 bg-signal-amber-50 border border-signal-amber-100 rounded-card">
              <VerifyCodeInput
                email={user?.email}
                initialSent
                cooldownKey="verifyCodeResendTimer:bookingGate"
                label="Confirm your email to publish"
                onSuccess={handleVerifySuccess}
                reassurance="Your booking page is saved — just confirm your email to publish."
              />
            </div>
          </div>
        )}

        {/* Send-failure notice (shown when the on-demand code send fails, so the
            input is never revealed for a code that was never sent) */}
        {!showVerificationCodeEntry && verificationSendError && (
          <div className="px-4 pb-4 sm:px-8">
            <p className="text-sm text-signal-red-600" role="alert">
              {verificationSendError}
            </p>
          </div>
        )}

        <FormFooter
          primaryCaption={!originalSlug ? "Save & Go Live" : "Save"}
          isLoading={isUpdating}
          disabled={!isDirty}
          requiresVerification={false}
        />

        {errors.title && (
          <div className="px-4 sm:px-6 lg:px-8 py-6">
            <AlertRed title={errors.title} message={errors.message} />
          </div>
        )}

        {success && (
          <div className="px-4 sm:px-6 lg:px-8 py-6">
            <AlertGreen
              title="Success"
              message={
                isNewUserActivation
                  ? "Your booking link is live! Share it with clients to start accepting appointments."
                  : "Booking link updated successfully"
              }
            />
          </div>
        )}
      </TabFormWrapper>
    </>
  );
};

export default BookingPageForm;
